import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTask, sendMessage, setEstado, setAssigneeDue, updateTask, useIsSaved, markMentionsReadInTask, markTaskRead, markTaskUnreadFrom, useTaskTags, setMyTaskTags, useTaskFollowers, addFollower, removeFollower, useTeamMembers, useMyMemberGroups, useMyTaskReminderMsgIds, useThreadReads, useTeamStages, setTaskStage } from './api';
import type { Tag } from './api';
import TagSelector from './TagSelector';
import FollowerPicker from './FollowerPicker';
import MessageBubble, { type ReaderRow, type MessageReadBy } from './MessageBubble';
import MessageEditor, { type MessageEditorHandle } from './MessageEditor';
import { useDialog } from '../ui/Dialogs';
import type { PendingAttachment, TaskEstado } from './types';

// Mismos pills que las tarjetas (TaskCard): azul "En curso", verde "Terminada".
const ESTADO_STYLE: Record<TaskEstado, { label: string; bg: string; text: string }> = {
  en_curso: { label: 'En curso', bg: '#eff5ff', text: '#1d4ed8' },
  resuelto: { label: 'Resuelto', bg: '#fef3c7', text: '#b45309' },
  terminada: { label: 'Terminada', bg: '#dcfce7', text: '#15803d' },
};

// Iconos de línea para los chips de la cabecera (color tenue, no compiten).
const iconStyle: CSSProperties = { color: '#94a3b8', flexShrink: 0 };
const CalIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
    <rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9.5h18" /><path d="M8 2.5v4" /><path d="M16 2.5v4" />
  </svg>
);
const TagIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
    <path d="M12.6 3H5a2 2 0 0 0-2 2v7.6a2 2 0 0 0 .6 1.4l7.4 7.4a2 2 0 0 0 2.8 0l5.6-5.6a2 2 0 0 0 0-2.8L13 3.6A2 2 0 0 0 12.6 3z" /><circle cx="7.5" cy="7.5" r="1.2" />
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
    <path d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

// Lápiz tenue que aparece en hover sobre un chip de dato editable (la clase
// .meta-chip__pencil controla la opacidad). Señala "pulsa para editar".
const PencilIcon = () => (
  <svg className="meta-chip__pencil" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1, flexShrink: 0 }}>
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

// Tres puntos "···": abre el menú con las propiedades editables del hilo.
const KebabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
  </svg>
);

export default function TareaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Volver a donde estabas (Team, Tags, Agenda, búsqueda…). Si entraste por un
  // enlace directo y no hay historial en la app, caemos en la bandeja.
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/bandeja');
  };
  const { profile, teams, activeTeamId, switchTeam } = useAuth();
  const { alert } = useDialog();
  const { task, messages, loading } = useTask(id);
  // La RLS deja leer hilos de cualquier espacio del que soy miembro, así que
  // un hilo de otro espacio (enlace, notificación, "atrás" tras cambiar de
  // espacio) se pintaría bajo el espacio activo. Si es mío, cambiamos a su
  // espacio (el shell se remonta con el correcto); si no, a la bandeja.
  useEffect(() => {
    if (!task?.team_id || !activeTeamId || task.team_id === activeTeamId) return;
    if (teams.some((t) => t.id === task.team_id)) switchTeam(task.team_id);
    else navigate('/bandeja', { replace: true });
  }, [task?.team_id, activeTeamId, teams, switchTeam, navigate]);
  const { saved, toggle: toggleSaved } = useIsSaved(id, profile?.id);
  const { tags: myTags, refetch: refetchTags } = useTaskTags(id, profile?.id);
  // Edición inline de mis tags en el propio hilo (cualquiera que lo vea, no
  // solo el creador). Las tags son privadas, así que no afecta a los demás.
  const [editingTags, setEditingTags] = useState(false);
  const [tagDraft, setTagDraft] = useState<Tag[]>([]);
  // Seguidores del hilo ("Etiquetar"): cualquiera del equipo puede gestionarlos.
  const { followers, refetch: refetchFollowers } = useTaskFollowers(id);
  // Notas sobre las que YO tengo un recordatorio pendiente (enciende el reloj).
  const { byMsg: reminderByMsg, refetch: refetchReminderMsgIds } = useMyTaskReminderMsgIds(id, profile?.id);
  const { members: teamMembers } = useTeamMembers();
  // Mapa id→nombre para resolver quién reaccionó (tooltip de los chips de emoji).
  const nameById = useMemo(
    () => new Map(teamMembers.map((m) => [m.id, m.nombre])),
    [teamMembers],
  );
  const { groups: myGroups } = useMyMemberGroups(profile?.id);

  // Acuse de lectura por respuesta ("ojito"): la audiencia de una nota es el
  // asignado + los seguidores (sin duplicar). Traemos su last_read_at en vivo
  // para pintar, en cada nota propia, quién la ha visto y quién la tiene
  // pendiente (audiencia menos el autor de la nota).
  const audience = useMemo<ReaderRow[]>(() => {
    const map = new Map<string, ReaderRow>();
    if (task?.assigned_to && task.assignee) {
      map.set(task.assigned_to, { id: task.assigned_to, nombre: task.assignee.nombre, color: task.assignee.color });
    }
    followers.forEach((f) => map.set(f.id, { id: f.id, nombre: f.nombre, color: f.color }));
    return [...map.values()];
  }, [task?.assigned_to, task?.assignee, followers]);
  const audienceIds = useMemo(() => audience.map((a) => a.id), [audience]);
  const threadReads = useThreadReads(id, audienceIds);
  const { stages } = useTeamStages();

  const readByForMessage = (authorId: string, createdAt: string): MessageReadBy => {
    const readers: ReaderRow[] = [];
    const pending: ReaderRow[] = [];
    audience.forEach((p) => {
      if (p.id === authorId) return; // el autor no se cuenta a sí mismo
      const at = threadReads[p.id];
      if (at && at >= createdAt) readers.push({ ...p, at });
      else pending.push(p);
    });
    return { readers, pending };
  };
  const [editingFollowers, setEditingFollowers] = useState(false);
  // Popover de "quién sigue" al pasar el ratón por el chip comprimido.
  const [showFollowers, setShowFollowers] = useState(false);
  // Fecha personal del asignado para su agenda.
  const [editingMyDate, setEditingMyDate] = useState(false);
  const [myDateDraft, setMyDateDraft] = useState('');
  const [myDateError, setMyDateError] = useState('');
  // Fecha de vencimiento del hilo (la edita el asignador pulsando el chip).
  const [editingDue, setEditingDue] = useState(false);
  const [dueDraft, setDueDraft] = useState('');
  const [dueError, setDueError] = useState('');
  // Menú "···" con las propiedades editables (etiquetas, fecha, seguidores).
  const [metaMenuOpen, setMetaMenuOpen] = useState(false);
  const metaMenuRef = useRef<HTMLSpanElement>(null);
  // Menú del botón "Publicar" (acción combinada publicar + cambiar estado).
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const publishMenuRef = useRef<HTMLDivElement>(null);
  // Casilla "Nota privada" del composer: la respuesta solo la verá su autor.
  // Se resetea al publicar o descartar para que no se quede pegada.
  const [privateNote, setPrivateNote] = useState(false);

  const startEditTags = () => { setTagDraft(myTags); setEditingTags(true); };
  const handleTagsChange = async (next: Tag[]) => {
    setTagDraft(next);
    if (!id || !profile?.id) return;
    await setMyTaskTags(id, next.map((t) => t.id), profile.id);
    refetchTags();
  };

  // Aplica el diff entre los seguidores actuales y la nueva selección.
  const handleFollowersChange = async (nextIds: string[]) => {
    if (!id) return;
    const next = new Set(nextIds);
    const current = new Set(followers.map((f) => f.id));
    const toAdd = nextIds.filter((x) => !current.has(x));
    const toRemove = followers.filter((f) => !next.has(f.id)).map((f) => f.id);
    try {
      for (const uid of toAdd) await addFollower(id, uid);
      for (const uid of toRemove) await removeFollower(id, uid);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo actualizar seguidores', message: msg });
    }
    refetchFollowers();
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<MessageEditorHandle>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Tramo marcado a mano como no leído en esta sesión. Cualquier respuesta
  // con created_at >= unreadFromAt aparece con el botón en azul. Si está en
  // null, el hilo está como leído (lo mismo que ve la campana).
  const [unreadFromAt, setUnreadFromAt] = useState<string | null>(null);
  // Nota a resaltar al llegar por enlace directo (#msg-<id>) desde un recordatorio.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const hashHandledRef = useRef<string | null>(null);

  const isMessageMarkedUnread = (createdAt: string) =>
    !!unreadFromAt && createdAt >= unreadFromAt;

  // Toggle: si la respuesta ya está en azul (= dentro del tramo no leído),
  // click vuelve el hilo entero a leído. Si está en gris, marca como no
  // leída desde aquí.
  const handleToggleUnread = (messageCreatedAt: string) => {
    if (!id || !profile?.id) return;
    if (isMessageMarkedUnread(messageCreatedAt)) {
      setUnreadFromAt(null);
      markTaskRead(id, profile.id).catch(() => {});
    } else {
      setUnreadFromAt(messageCreatedAt);
      markTaskUnreadFrom(id, profile.id, messageCreatedAt).catch(() => {});
    }
  };

  useEffect(() => {
    // Si venimos a una nota concreta (#msg-<id>), no forzamos el scroll al final:
    // el efecto de abajo se encarga de centrar y resaltar esa nota.
    if (location.hash.startsWith('#msg-')) return;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, location.hash]);

  // Enlace directo a una nota: cuando esa nota ya está cargada, la centramos y
  // la resaltamos un par de segundos. Solo una vez por hash. No usamos cleanup
  // atado a `messages` a propósito: un re-render por realtime no debe cancelar
  // el scroll ni recortar el resaltado.
  useEffect(() => {
    const hash = location.hash;
    if (!hash.startsWith('#msg-') || hashHandledRef.current === hash) return;
    const msgId = hash.slice('#msg-'.length);
    if (!messages.some((m) => m.id === msgId)) return;
    hashHandledRef.current = hash;
    setHighlightId(msgId);
    window.setTimeout(() => {
      document.getElementById(`msg-${msgId}`)?.scrollIntoView({ block: 'center' });
    }, 80);
    window.setTimeout(() => {
      setHighlightId((cur) => (cur === msgId ? null : cur));
    }, 2400);
  }, [location.hash, messages]);

  // Marca como leídas las menciones del usuario en este hilo al entrar
  // (no-op si no hay no leídas).
  useEffect(() => {
    if (!id || !profile?.id) return;
    markMentionsReadInTask(id, profile.id).catch(() => {});
  }, [id, profile?.id]);

  // Marca el hilo como visto para que la campana de actividad lo descarte.
  // Lo hacemos al entrar Y cuando llegan mensajes nuevos mientras lo tienes
  // abierto, para que no se acumule un badge fantasma de respuestas en vivo.
  // Excepción: si el usuario ya ha marcado a mano una respuesta como no
  // leída, respetamos esa intención y no auto-marcamos nada.
  useEffect(() => {
    if (!id || !profile?.id) return;
    if (unreadFromAt) return;
    markTaskRead(id, profile.id).catch(() => {});
  }, [id, profile?.id, messages.length, unreadFromAt]);

  // Cierra el menú "···" al pulsar fuera. Debe ir aquí, junto al resto de hooks
  // y ANTES de cualquier return temprano (regla de hooks).
  useEffect(() => {
    if (!metaMenuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (metaMenuRef.current && !metaMenuRef.current.contains(e.target as Node)) setMetaMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [metaMenuOpen]);

  // Cierra el menú del botón "Publicar" al pulsar fuera.
  useEffect(() => {
    if (!publishMenuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (publishMenuRef.current && !publishMenuRef.current.contains(e.target as Node)) setPublishMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [publishMenuOpen]);

  if (loading && !task) {
    return <div style={{ padding: 40, color: '#64748b' }}>Cargando…</div>;
  }
  if (!task || !id) {
    return (
      <div style={{ padding: 40 }}>
        <p style={{ color: '#64748b' }}>Este hilo no existe o fue eliminado.</p>
        <Link to="/bandeja" style={{ color: '#3b82f6' }}>Volver a la bandeja</Link>
      </div>
    );
  }

  const estado = ESTADO_STYLE[task.estado];
  const isCreator = task.created_by === profile?.id;
  const isAssignee = !!profile?.id && task.assigned_to === profile.id;

  // Acción de cierre que puede "viajar" con la publicación desde el composer:
  // el asignado marca resuelto; el creador da por terminada. Réplica de los
  // botones de estado de la cabecera, para no tener que subir a buscarlos al
  // terminar de escribir la nota de cierre.
  const resolveAction: { estado: TaskEstado; label: string } | null =
    task.estado === 'en_curso' && isAssignee && !isCreator
      ? { estado: 'resuelto', label: 'Publicar y marcar resuelto' }
      : task.estado === 'en_curso' && isCreator
        ? { estado: 'terminada', label: 'Publicar y dar por terminada' }
        : task.estado === 'resuelto' && isCreator
          ? { estado: 'terminada', label: 'Publicar y dar por terminada' }
          : null;

  const handleSend = async ({ body, attachments }: { body: string; attachments: PendingAttachment[] }) => {
    if (!profile) return;
    await sendMessage({ task_id: id, author_id: profile.id, body, attachments, is_private: privateNote });
  };

  const startEditMyDate = () => { setMyDateDraft(task.assignee_due ?? ''); setMyDateError(''); setEditingMyDate(true); };
  const saveMyDate = async () => {
    setMyDateError('');
    try { await setAssigneeDue(id, myDateDraft || null); setEditingMyDate(false); }
    catch (e) { setMyDateError(e instanceof Error ? e.message : 'No se pudo guardar'); }
  };
  const clearMyDate = async () => {
    setMyDateError('');
    try { await setAssigneeDue(id, null); setEditingMyDate(false); }
    catch (e) { setMyDateError(e instanceof Error ? e.message : 'No se pudo guardar'); }
  };

  const startEditDue = () => { setDueDraft(task.fecha_vencimiento ?? ''); setDueError(''); setEditingDue(true); };
  const saveDue = async () => {
    setDueError('');
    try { await updateTask(id, { fecha_vencimiento: dueDraft || null }); setEditingDue(false); }
    catch (e) { setDueError(e instanceof Error ? e.message : 'No se pudo guardar'); }
  };
  const clearDue = async () => {
    setDueError('');
    try { await updateTask(id, { fecha_vencimiento: null }); setEditingDue(false); }
    catch (e) { setDueError(e instanceof Error ? e.message : 'No se pudo guardar'); }
  };

  // Abre un editor de propiedad desde el menú "···" (cierra los demás y el menú).
  const openMeta = (which: 'tags' | 'due' | 'mydate' | 'followers') => {
    setEditingTags(false); setEditingDue(false); setEditingMyDate(false); setEditingFollowers(false);
    if (which === 'tags') startEditTags();
    else if (which === 'due') startEditDue();
    else if (which === 'mydate') startEditMyDate();
    else setEditingFollowers(true);
    setMetaMenuOpen(false);
  };

  // Etapa del tablero: la cambia el asignado (o el creador si no hay asignado)
  // y solo tiene sentido mientras el hilo está en curso.
  const currentStage = stages.find((s) => s.id === task.stage_id) ?? null;
  const canMoveStage = task.assigned_to ? isAssignee : isCreator;
  const changeStage = async (stageId: string | null) => {
    try {
      await setTaskStage(id, stageId);
    } catch (err) {
      alert({ title: 'No se pudo cambiar la etapa', message: err instanceof Error ? err.message : undefined });
    }
  };

  const changeEstado = async (nuevo: TaskEstado) => {
    try {
      await setEstado(id, nuevo);
    } catch (err) {
      alert({ title: 'No se pudo cambiar el estado', message: err instanceof Error ? err.message : undefined });
    }
  };

  const submitFromComposer = async (resolveTo?: TaskEstado) => {
    const editor = editorRef.current;
    if (!editor || editor.isEmpty()) return;
    const { body, attachments } = editor.getContent();
    setSending(true);
    setPublishMenuOpen(false);
    try {
      await handleSend({ body, attachments });
      if (resolveTo) await setEstado(id, resolveTo);
      editor.clear();
      setPrivateNote(false);
      setComposerOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'Error enviando', message: msg });
    } finally {
      setSending(false);
    }
  };

  const discardComposer = () => {
    editorRef.current?.clear();
    setPrivateNote(false);
    setComposerOpen(false);
  };

  return (
    <div style={styles.wrap}>
      <header style={styles.topbar}>
        <button onClick={goBack} style={styles.backBtn}>
          ‹ Volver
        </button>
        <div style={styles.topbarTitle} title={task.asunto}>
          {task.asunto}
        </div>
        <div style={styles.topbarActions}>
          <button
            onClick={toggleSaved}
            title={saved ? 'Quitar de guardados' : 'Guardar'}
            style={{ ...styles.iconBtn, color: saved ? '#2563eb' : '#64748b' }}
          >
            <svg
              width="19" height="19" viewBox="0 0 24 24"
              fill={saved ? 'currentColor' : 'none'}
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
            </svg>
          </button>
          {isCreator && (
            <button
              onClick={() => navigate(`/tareas/${id}/editar`)}
              title="Editar hilo"
              style={styles.iconBtn}
            >
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
              </svg>
            </button>
          )}
          {/* Acciones de estado según rol y estado actual:
              En curso → asignado "Marca resuelto"; creador "Da por terminada".
              Resuelto → creador valida (terminada) o devuelve; asignado retira.
              Terminada → creador reabre. */}
          {task.estado === 'en_curso' && isCreator && (
            <button onClick={() => changeEstado('terminada')} style={{ ...styles.estadoBtn, background: '#16a34a', color: '#fff' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              Marcar terminada
            </button>
          )}
          {task.estado === 'en_curso' && isAssignee && !isCreator && (
            <button onClick={() => changeEstado('resuelto')} style={{ ...styles.estadoBtn, background: '#f59e0b', color: '#fff' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              Marcar resuelto
            </button>
          )}
          {task.estado === 'resuelto' && isCreator && (
            <>
              <button onClick={() => changeEstado('en_curso')} style={{ ...styles.estadoBtn, background: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}>
                Devolver a en curso
              </button>
              <button onClick={() => changeEstado('terminada')} style={{ ...styles.estadoBtn, background: '#16a34a', color: '#fff' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                Dar por terminada
              </button>
            </>
          )}
          {task.estado === 'resuelto' && isAssignee && !isCreator && (
            <button onClick={() => changeEstado('en_curso')} style={{ ...styles.estadoBtn, background: '#e2e8f0', color: '#475569' }}>
              Retirar resolución
            </button>
          )}
          {task.estado === 'terminada' && isCreator && (
            <button onClick={() => changeEstado('en_curso')} style={{ ...styles.estadoBtn, background: '#e2e8f0', color: '#475569' }}>
              Reabrir
            </button>
          )}
        </div>
      </header>

      <div ref={scrollRef} style={styles.scroll}>
        <div style={styles.contentMax}>
          <section style={styles.hero}>
            <span style={{ ...styles.estadoPill, background: estado.bg, color: estado.text }}>
              {estado.label}
            </span>
            {task.estado === 'en_curso' && stages.length > 0 && (
              canMoveStage ? (
                <select
                  value={currentStage?.id ?? ''}
                  onChange={(e) => changeStage(e.target.value || null)}
                  title="Etapa del tablero"
                  style={{ ...styles.stageSelect, borderColor: currentStage?.color ?? '#e2e8f0' }}
                >
                  <option value="">Sin etapa</option>
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              ) : currentStage ? (
                <span style={{ ...styles.stagePill, borderColor: currentStage.color }} title="Etapa del tablero">
                  {currentStage.nombre}
                </span>
              ) : null
            )}
            <h1 style={styles.heroTitle}>{task.asunto}</h1>

            {/* Propiedades del hilo estilo Twist: chips sutiles en una fila.
                Personas con avatar, iconos en vez de etiquetas, y los editables
                como píldoras que despliegan su picker a ancho completo. */}
            <div style={styles.chipRow}>
              {task.creator && (
                <span style={styles.chip} title="Creado por">
                  <span style={styles.chipDim}>De:</span>
                  <span style={{ ...styles.chipMono, background: task.creator.color }}>
                    {(task.creator.nombre.charAt(0) || '?').toUpperCase()}
                  </span>
                  <strong style={{ color: task.creator.color, fontWeight: 600 }}>{task.creator.nombre}</strong>
                </span>
              )}
              {task.assignee && (
                <span style={styles.chip} title="Asignado">
                  <span style={styles.chipDim}>Para:</span>
                  <span style={{ ...styles.chipMono, background: task.assignee.color }}>
                    {(task.assignee.nombre.charAt(0) || '?').toUpperCase()}
                  </span>
                  <strong style={{ color: task.assignee.color, fontWeight: 600 }}>{task.assignee.nombre}</strong>
                </span>
              )}
              {/* Vencimiento del hilo: es un DATO. El asignador lo edita
                  pulsándolo (lápiz en hover); el resto lo ve en solo lectura.
                  Si no hay fecha, se añade desde el menú "···". */}
              {task.fecha_vencimiento && (
                isCreator ? (
                  <button
                    type="button"
                    className="meta-chip--editable"
                    onClick={() => (editingDue ? setEditingDue(false) : startEditDue())}
                    style={{ ...styles.chip, ...(editingDue ? styles.chipActive : null), fontFamily: 'inherit' }}
                    title="Fecha de vencimiento · clic para cambiarla"
                  >
                    <CalIcon />{formatDate(task.fecha_vencimiento)}<PencilIcon />
                  </button>
                ) : (
                  <span style={styles.chip} title="Fecha de vencimiento">
                    <CalIcon />{formatDate(task.fecha_vencimiento)}
                  </span>
                )
              )}

              {/* Seguidores: DATO (icono + contador; hover despliega quién sigue,
                  clic edita). Sin seguidores no se pinta; se añaden desde "···". */}
              {followers.length > 0 && (
                <span
                  style={styles.followWrap}
                  onMouseEnter={() => setShowFollowers(true)}
                  onMouseLeave={() => setShowFollowers(false)}
                >
                  <button
                    type="button"
                    className="meta-chip--editable"
                    onClick={() => setEditingFollowers((v) => !v)}
                    style={{ ...styles.chip, ...(editingFollowers ? styles.chipActive : null), fontFamily: 'inherit' }}
                    title="Seguidores · clic para editar"
                  >
                    <EyeIcon /><span style={{ fontWeight: 700 }}>{followers.length}</span>
                  </button>
                  {showFollowers && (
                    <div style={styles.followPop}>
                      <div style={styles.followPopTitle}>Siguiendo este hilo</div>
                      {followers.map((f) => (
                        <div key={f.id} style={styles.followPopRow}>
                          <span style={{ ...styles.chipMono, background: f.color }}>
                            {(f.nombre.charAt(0) || '?').toUpperCase()}
                          </span>
                          <span style={styles.followPopName}>{f.nombre}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </span>
              )}

              {/* Tags (privadas): los chips son DATO (clic = filtrar por la tag).
                  Añadir o quitar vive en el menú "···". */}
              {!editingTags && myTags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`/bandeja?tag=${t.id}`)}
                  style={{ ...styles.tagChip, background: `${t.color}1a`, color: t.color, borderColor: t.color }}
                >
                  {t.nombre}
                </button>
              ))}

              {/* Fecha personal del asignado: DATO editable al clic. Si no la ha
                  puesto, se añade desde el menú "···". */}
              {isAssignee && task.assignee_due && (
                <button
                  type="button"
                  className="meta-chip--editable"
                  onClick={() => (editingMyDate ? setEditingMyDate(false) : startEditMyDate())}
                  style={{ ...styles.chip, ...(editingMyDate ? styles.chipActive : null), fontFamily: 'inherit' }}
                  title="Tu fecha personal en la Agenda · clic para cambiarla"
                >
                  <CalIcon /><span style={{ color: '#7c3aed', fontWeight: 600 }}>{formatDate(task.assignee_due)}</span><PencilIcon />
                </button>
              )}

              {/* El asignador ve, de forma discreta, la fecha que se ha fijado el asignado. */}
              {isCreator && !isAssignee && task.assignee_due && (
                <span style={styles.chip} title="Fecha que se ha fijado el asignado">
                  <CalIcon /><span style={{ color: '#7c3aed', fontWeight: 600 }}>{formatDate(task.assignee_due)}</span>
                </span>
              )}

              {/* Menú "···": reúne las propiedades editables (etiquetas, fecha,
                  seguidores). Único punto para añadir lo que aún no existe. */}
              <span ref={metaMenuRef} style={styles.kebabWrap}>
                <button
                  type="button"
                  onClick={() => setMetaMenuOpen((v) => !v)}
                  style={{ ...styles.kebabBtn, ...(metaMenuOpen ? styles.chipActive : null) }}
                  title="Editar propiedades del hilo"
                  aria-label="Editar propiedades del hilo"
                >
                  <KebabIcon />
                </button>
                {metaMenuOpen && (
                  <div style={styles.metaMenu}>
                    <div style={styles.metaMenuLabel}>Editar propiedades</div>
                    <button type="button" className="kebab-item" style={styles.metaMenuItem} onClick={() => openMeta('tags')}>
                      <span style={styles.metaMenuIcon}><TagIcon /></span>Etiquetas
                    </button>
                    {isCreator && (
                      <button type="button" className="kebab-item" style={styles.metaMenuItem} onClick={() => openMeta('due')}>
                        <span style={styles.metaMenuIcon}><CalIcon /></span>Fecha de vencimiento
                      </button>
                    )}
                    {isAssignee && (
                      <button type="button" className="kebab-item" style={styles.metaMenuItem} onClick={() => openMeta('mydate')}>
                        <span style={styles.metaMenuIcon}><CalIcon /></span>Mi fecha
                      </button>
                    )}
                    <button type="button" className="kebab-item" style={styles.metaMenuItem} onClick={() => openMeta('followers')}>
                      <span style={styles.metaMenuIcon}><EyeIcon /></span>Seguidores
                    </button>
                  </div>
                )}
              </span>
            </div>

            {/* Editores desplegados a ancho completo, SIEMPRE bajo la fila de
                chips (no en medio), para que la fila no se rompa. */}
            {editingTags && (
              <div style={styles.editorBelow}>
                <TagSelector selected={tagDraft} onChange={handleTagsChange} />
                <button type="button" onClick={() => setEditingTags(false)} style={styles.tagDoneBtn}>Listo</button>
              </div>
            )}
            {editingFollowers && (
              <div style={styles.editorBelow}>
                <FollowerPicker
                  value={followers.map((f) => f.id)}
                  onChange={handleFollowersChange}
                  members={teamMembers}
                  groups={myGroups}
                  placeholder="+ Etiquetar a alguien del equipo"
                />
                <button type="button" onClick={() => setEditingFollowers(false)} style={styles.tagDoneBtn}>Listo</button>
              </div>
            )}
            {editingMyDate && (
              <div style={styles.editorBelow}>
                <div style={styles.dateEditRow}>
                  <input
                    type="date"
                    value={myDateDraft}
                    max={task.fecha_vencimiento ?? undefined}
                    onChange={(e) => setMyDateDraft(e.target.value)}
                    className="st-field"
                    style={styles.myDateInput}
                  />
                  <button type="button" onClick={saveMyDate} style={styles.myDateSave}>Guardar</button>
                  {task.assignee_due && (
                    <button type="button" onClick={clearMyDate} style={styles.chipBtn}>Quitar</button>
                  )}
                  <button type="button" onClick={() => setEditingMyDate(false)} style={styles.chipBtn}>Cancelar</button>
                </div>
                {myDateError && <span style={styles.myDateError}>{myDateError}</span>}
              </div>
            )}
            {editingDue && (
              <div style={styles.editorBelow}>
                <div style={styles.dateEditRow}>
                  <input
                    type="date"
                    value={dueDraft}
                    onChange={(e) => setDueDraft(e.target.value)}
                    className="st-field"
                    style={styles.myDateInput}
                  />
                  <button type="button" onClick={saveDue} style={styles.myDateSave}>Guardar</button>
                  {task.fecha_vencimiento && (
                    <button type="button" onClick={clearDue} style={styles.chipBtn}>Quitar</button>
                  )}
                  <button type="button" onClick={() => setEditingDue(false)} style={styles.chipBtn}>Cancelar</button>
                </div>
                {dueError && <span style={styles.myDateError}>{dueError}</span>}
              </div>
            )}
          </section>

          <section>
            {messages.length === 0 ? (
              <p style={{ color: '#94a3b8', textAlign: 'center', marginTop: 32 }}>
                Aún no hay mensajes. Escribe el primero abajo.
              </p>
            ) : (
              messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  isMine={m.author_id === profile?.id}
                  currentUserId={profile?.id}
                  nameById={nameById}
                  readBy={m.author_id === profile?.id && !m.is_private ? readByForMessage(m.author_id, m.created_at) : undefined}
                  markedUnread={isMessageMarkedUnread(m.created_at)}
                  onMarkUnreadFrom={handleToggleUnread}
                  highlighted={highlightId === m.id}
                  hasReminder={reminderByMsg.has(m.id)}
                  reminderId={reminderByMsg.get(m.id)}
                  onReminderCreated={refetchReminderMsgIds}
                />
              ))
            )}
          </section>

          <div style={{ height: 96 }} />
        </div>
      </div>

      <div style={styles.composerWrap}>
        <div style={styles.composerMax}>
          {composerOpen ? (
            <div>
              <MessageEditor
                ref={editorRef}
                taskId={id}
                showSendButton={false}
                minHeight={80}
                onSend={() => {}}
              />
              <div style={styles.composerActions}>
                {/* Nota privada: solo la ve quien la escribe. No notifica ni
                    genera menciones; el resto del hilo ni sabe que existe. */}
                <label
                  style={{ ...styles.privateToggle, ...(privateNote ? styles.privateToggleOn : null) }}
                  title="Solo tú verás esta nota. No avisa a nadie ni cuenta como respuesta."
                >
                  <input
                    type="checkbox"
                    checked={privateNote}
                    onChange={(e) => setPrivateNote(e.target.checked)}
                    disabled={sending}
                    style={styles.privateCheckbox}
                  />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  Nota privada
                  <span style={styles.privateHint}>· solo la ves tú</span>
                </label>
                <button type="button" onClick={discardComposer} style={styles.btnGhost}>
                  Descartar
                </button>
                {resolveAction ? (
                  <div ref={publishMenuRef} style={styles.publishWrap}>
                    <button
                      type="button"
                      onClick={() => submitFromComposer()}
                      disabled={sending}
                      style={styles.publishMain}
                    >
                      {sending ? 'Enviando…' : 'Publicar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPublishMenuOpen((v) => !v)}
                      disabled={sending}
                      style={{ ...styles.publishCaret, ...(publishMenuOpen ? { background: '#15803d' } : null) }}
                      aria-label="Más opciones de publicación"
                      aria-haspopup="menu"
                      aria-expanded={publishMenuOpen}
                      title="Más opciones"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                        <path d="M18 15l-6-6-6 6" />
                      </svg>
                    </button>
                    {publishMenuOpen && (
                      <div style={styles.publishMenu} role="menu">
                        <button
                          type="button"
                          className="kebab-item"
                          style={styles.publishMenuItem}
                          onClick={() => submitFromComposer(resolveAction.estado)}
                          disabled={sending}
                        >
                          <span style={styles.publishMenuIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                          </span>
                          {resolveAction.label}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => submitFromComposer()}
                    disabled={sending}
                    style={styles.btnPrimary}
                  >
                    {sending ? 'Enviando…' : 'Publicar'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              style={styles.composerCollapsed}
            >
              <span style={{ color: '#94a3b8' }}>Escribe un mensaje…</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return input;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${String(d.getFullYear()).slice(-2)}`;
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#fff',
    boxSizing: 'border-box',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 20px',
    borderBottom: '1px solid #e2e8f0',
    background: '#fff',
    minHeight: 52,
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 14,
    padding: '4px 8px',
    fontWeight: 500,
  },
  topbarTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '0 12px',
  },
  topbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    padding: '6px 8px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  estadoBtn: {
    border: 'none',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    background: '#fff',
  },
  contentMax: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '32px 40px 0',
  },
  hero: {
    marginBottom: 24,
  },
  estadoPill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.3,
    marginBottom: 12,
  },
  stagePill: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    border: '1px solid #e2e8f0',
    marginLeft: 8,
    marginBottom: 12,
  },
  stageSelect: {
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    background: '#fff',
    border: '1px solid #e2e8f0',
    marginLeft: 8,
    marginBottom: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#0f172a',
    margin: 0,
    lineHeight: 1.25,
    whiteSpace: 'pre-wrap',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
  },
  // Chip estático (persona, fecha): muy sutil, no compite con el contenido.
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 11px',
    borderRadius: 999,
    background: '#f8fafc',
    border: '1px solid #eef2f6',
    fontSize: 13,
    color: '#475569',
  },
  // Chip clicable (editar tags/seguidores/fecha).
  chipBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 11px',
    borderRadius: 999,
    background: '#fff',
    border: '1px solid #e2e8f0',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
  },
  chipActive: { background: '#eff5ff', borderColor: '#bfdbfe', color: '#1d4ed8' },
  chipDim: { color: '#94a3b8', fontWeight: 500 },
  chipMono: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 9,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Chip de seguidores comprimido + popover "quién sigue" en hover.
  followWrap: { position: 'relative', display: 'inline-flex' },
  followPop: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    zIndex: 50,
    minWidth: 180,
    maxWidth: 240,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    padding: '8px 6px',
  },
  followPopTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '2px 8px 6px',
  },
  followPopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 8px',
    borderRadius: 7,
  },
  followPopName: {
    fontSize: 13.5,
    color: '#334155',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Menú "···" de la cabecera con las propiedades editables.
  kebabWrap: { position: 'relative', display: 'inline-flex' },
  kebabBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 28,
    borderRadius: 8,
    background: '#fff',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    cursor: 'pointer',
  },
  metaMenu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    zIndex: 50,
    minWidth: 210,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    padding: 6,
  },
  metaMenuLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '6px 10px 4px',
  },
  metaMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    width: '100%',
    padding: '9px 10px',
    borderRadius: 7,
    fontSize: 14,
    color: '#0f172a',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  metaMenuIcon: { display: 'flex', alignItems: 'center', color: '#64748b' },
  // Editor desplegado bajo la fila de chips (a ancho acotado).
  editorBelow: { width: '100%', maxWidth: 460, marginTop: 12 },
  dateEditRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  myDateInput: { border: '1px solid #e2e8f0', borderRadius: 8, padding: '5px 10px', fontSize: 13, outline: 'none', background: '#fff' },
  myDateSave: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  myDateError: { color: '#dc2626', fontSize: 12.5, width: '100%' },
  tagChip: {
    padding: '3px 11px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid',
    cursor: 'pointer',
  },
  tagDoneBtn: {
    marginTop: 8,
    padding: '6px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    background: '#0f172a',
    color: '#fff',
    cursor: 'pointer',
  },
  composerWrap: {
    borderTop: '1px solid #e2e8f0',
    background: '#fff',
    padding: '14px 0',
  },
  composerMax: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '0 40px',
  },
  composerCollapsed: {
    width: '100%',
    textAlign: 'left',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 14,
    cursor: 'text',
    color: '#94a3b8',
  },
  composerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  // Casilla "Nota privada" a la izquierda de los botones del composer.
  privateToggle: {
    marginRight: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid transparent',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
    userSelect: 'none',
  },
  privateToggleOn: {
    color: '#b45309',
    background: '#fffbeb',
    border: '1px solid #fcd34d',
  },
  privateCheckbox: { margin: 0, accentColor: '#d97706', cursor: 'pointer' },
  privateHint: { fontWeight: 500, color: '#94a3b8' },
  btnGhost: {
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    color: '#475569',
    fontSize: 13,
    fontWeight: 600,
  },
  btnPrimary: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '9px 20px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  // Split button "Publicar" + caret con la acción combinada (publicar y resolver).
  publishWrap: { position: 'relative', display: 'inline-flex' },
  publishMain: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px 0 0 8px',
    padding: '9px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  publishCaret: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderLeft: '1px solid rgba(255,255,255,0.28)',
    borderRadius: '0 8px 8px 0',
    padding: '9px 9px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  publishMenu: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    right: 0,
    zIndex: 50,
    minWidth: 230,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    padding: 6,
  },
  publishMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '9px 10px',
    borderRadius: 7,
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  publishMenuIcon: { display: 'flex', alignItems: 'center', color: '#16a34a' },
};
